import { css, jsx } from '@emotion/react';
import { Typography, Icon } from '@zapier/design-system';
import React, { useContext } from 'react';
import { TagGroupContext, Tag, Button } from 'react-aria-components';

const FilterChipStyles = {
  root: css`
    display: flex;
    align-items: center;
    height: var(--zds-size-small);
    border: var(--zds-border-weak);
    border-radius: var(--zds-radius-default);
    padding-top: var(--zds-space-2);
    /* Baseline right padding. Children will apply additional spacing to meet spacing specified in designs. */
    padding-right: var(--zds-space-2);
    padding-bottom: var(--zds-space-2);
    /* Baseline left padding. Children will apply additional spacing to meet spacing specified in designs. */
    padding-left: var(--zds-space-2);
    cursor: default;

    &[data-focus-visible] {
      outline: var(--zds-border-focus);
      outline-offset: 2px;
    }

    &[data-selectable] {
      cursor: pointer;
    }

    &[data-selected] {
      background-color: var(--zds-background-selected);
      border-color: var(--zds-stroke-selected);
    }

    &[data-selectable]:not([data-disabled]) {
      cursor: pointer;
    }

    &[data-variant='neutral'] {
      background-color: var(--zds-background-stronger);

      &[data-selected] {
        background-color: var(--zds-background-default);
        border-color: var(--var-zds-stroke-stronger);
      }
    }

    &[data-disabled] {
      background-color: var(--zds-background-strongest);
      border: none;
      cursor: not-allowed;
    }

    &[data-size='medium'] {
      height: var(--zds-size-medium);
      padding-top: var(--zds-space-6);
      padding-bottom: var(--zds-space-6);
    }

    /* Icon overrides */
    &[data-selected] [data-testid='iconContainer'] {
      color: var(--zds-text-link);
    }

    & span[data-testid='iconContainer'] {
      display: flex;
      justify-content: center;
      align-items: center;
      height: var(--zds-size-16);
      width: var(--zds-size-16);
    }

    &[data-size='medium'] span[data-testid='iconContainer'] {
      height: var(--zds-size-20);
      width: var(--zds-size-20);
    }
  `,
  titleLabelWrapper: css`
    display: inline-flex;
    gap: var(--zds-space-2);
    margin-left: var(--zds-space-4);
    margin-right: var(--zds-space-2);

    [data-size='medium'] & {
      gap: var(--zds-space-4);
      margin-left: var(--zds-space-6);
      margin-right: var(--zds-space-6);
    }
  `,
  title: css`
    ${Typography.Body3Multiline}
    color: var(--zds-text-default);

    [data-size='compact'] & {
      ${Typography.MinimalPrint3}
    }

    [data-size='medium'] & {
      ${Typography.Body3}
    }

    [data-selected] & {
      color: var(--zds-text-link);
    }

    [data-variant='neutral'][data-selected] & {
      color: var(--zds-text-stronger);
    }
  `,
  label: css`
    ${Typography.Body1Multiline}
    color: var(--zds-text-weakest);

    [data-size='compact'] & {
      ${Typography.MinimalPrint1}
    }

    [data-size='medium'] & {
      ${Typography.Body1}
    }

    [data-selected] & {
      color: var(--zds-text-link);
    }

    [data-variant='neutral'][data-selected] & {
      color: var(--zds-text-weakest);
    }
  `,
  removalButton: css`
    /* Applies the same size to both the button and the Icon because otherwise button gets extra height */
    height: var(--zds-size-20);
    width: var(--zds-size-20);

    & [data-testid='iconContainer'] {
      height: var(--zds-size-20) !important;
      width: var(--zds-size-20) !important;
    }

    [data-size='compact'] & {
      height: var(--zds-size-16);
      width: var(--zds-size-16);

      & [data-testid='iconContainer'] {
        height: var(--zds-size-16) !important;
        width: var(--zds-size-16) !important;
      }
    }
  `
};
const FilterChip = ({ children, id, isDisabled, label, showsCheckmarkWhenSelected = false, size: sizeFromProps = "small", title, variant: variantFromProps = "primary" }) => {
  var _a, _b, _c;
  const tagGroupContext = useContext(TagGroupContext);
  const size = (_a = tagGroupContext.size) !== null && _a !== void 0 ? _a : sizeFromProps;
  const variant = (_b = tagGroupContext.variant) !== null && _b !== void 0 ? _b : variantFromProps;
  return jsx(Tag, { css: FilterChipStyles.root, "data-selectable": (_c = tagGroupContext === null || tagGroupContext === void 0 ? void 0 : tagGroupContext.selectionMode) !== null && _c !== void 0 ? _c : void 0, "data-size": size, "data-variant": variant, "data-zds": true, id, isDisabled, textValue: label ? `${title} ${label}` : title }, ({ allowsRemoving, isSelected }) => jsx(
    React.Fragment,
    null,
    isSelected && showsCheckmarkWhenSelected && jsx(Icon, { name: "formCheck" }),
    typeof children === "function" ? children() : children,
    title && jsx(
      "div",
      { css: FilterChipStyles.titleLabelWrapper, "data-zds": true },
      jsx("span", { css: FilterChipStyles.title, "data-zds": true }, title),
      jsx("span", { css: FilterChipStyles.label, "data-zds": true }, label)
    ),
    allowsRemoving && jsx(
      Button,
      { css: FilterChipStyles.removalButton, "data-zds": true, slot: "remove" },
      jsx(Icon, { color: "StrokeStronger", name: "formX" })
    )
  ));
};
const FilterChipIcon = ({ name }) => jsx(Icon, { name });

export { FilterChip, FilterChipIcon };
//# sourceMappingURL=FilterChip.es.js.map
